import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { security } from '../../utils/security';
import { supabase } from '../../utils/supabaseClient';

export function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!security.checkRateLimit()) {
        throw new Error('Please wait before submitting again.');
      }

      // Validate email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new Error('Please enter a valid email address.');
      }

      // Save to Supabase
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .insert([
          {
            email: email.toLowerCase(),
            status: 'active'
          }
        ]);

      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('This email is already subscribed to our newsletter.');
        }
        throw error;
      }

      // Record the attempt
      security.recordAttempt();
      
      showToast('Successfully subscribed to the newsletter!', 'success');
      setEmail('');
    } catch (error) {
      if (error instanceof Error) {
        showToast(error.message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold">Subscribe to Our Newsletter</h3>
      </div>
      <p className="text-gray-600 mb-4">
        Get the latest wedding planning tips and updates delivered to your inbox.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          required
        />
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
    </div>
  );
}